import { ActionReducerMapBuilder, createAsyncThunk } from "@reduxjs/toolkit";
import { graphqlRequest } from "context/graphql/functions";
import { AppAsyncThunkConfig } from "store";
import { StockHoldingFileType } from "operations/schema/schema";
import { State } from "./files.store";

export const createAppAsyncThunk = createAsyncThunk.withTypes<AppAsyncThunkConfig>();

function uint8ToBase64(u8Arr: Uint8Array): string {
  const CHUNK_SIZE = 0x8000; 
  let result = "";
  for (let i = 0; i < u8Arr.length; i += CHUNK_SIZE) {
    const chunk = u8Arr.subarray(i, i + CHUNK_SIZE);
    result += String.fromCharCode(...chunk);
  }
  return btoa(result);
}

export const asyncQueries = {
  getStockHoldingFiles: createAppAsyncThunk(
    "files/getStockHoldingFiles",
    async (
      { stockHoldingId }: { stockHoldingId: string },
      { rejectWithValue, extra: { sdk } }
    ) => {
      const { data, errors } = await graphqlRequest(sdk.getStockHoldingFiles, {
        variables: { stockHoldingId },
      });

      if (errors) return rejectWithValue(errors);
      if (!data?.stockHoldingFiles) return rejectWithValue("something went wrong");

      const convertedFiles = data.stockHoldingFiles.map(
        (f: StockHoldingFileType) => {
          const byteArray = new Uint8Array(f.data);
          const fileData = uint8ToBase64(byteArray);
          return {
            id: f.id,
            name: f.name,
            description: f.description ?? "",
            data: fileData,
            size: byteArray.length,
            isFullSize: false,
            isUnavailable: f.isUnavailable ?? false,
          };
        }
      );

      return convertedFiles;
    }
  ),
  getCustomerFiles: createAppAsyncThunk(
    "files/getCustomerFiles",
    async (
      { customerId }: { customerId: string },
      { rejectWithValue, extra: { sdk } }
    ) => {
      const { data, errors } = await graphqlRequest(sdk.getCustomerFiles, {
        variables: { customerId },
      });

      if (errors) return rejectWithValue(errors);
      if (!data?.customerFiles) return rejectWithValue("something went wrong");

      const convertedFiles = data.customerFiles.map((f: any) => {
        const byteArray = new Uint8Array(f.data);
        const fileData = uint8ToBase64(byteArray);
        return {
          id: f.id,
          name: f.name,
          description: f.description ?? "",
          data: fileData,
          size: byteArray.length,
          isFullSize: false,
        };
      });

      return convertedFiles;
    }
  ),
};

export const queryBuilder = (builder: ActionReducerMapBuilder<State>) => {
  builder.addCase(asyncQueries.getStockHoldingFiles.pending, (state) => {
    state.loadingFiles = true;
    state.files = [];
    return state;
  });

  builder.addCase(asyncQueries.getStockHoldingFiles.rejected, (state) => {
    state.loadingFiles = false;
    return state;
  });

  builder.addCase(
    asyncQueries.getStockHoldingFiles.fulfilled,
    (state, { payload }) => {
      state.loadingFiles = false;
      state.files = payload;
      return state;
    }
  );

  builder.addCase(asyncQueries.getCustomerFiles.pending, (state) => {
    state.loadingCustomerFiles = true;
    state.customerFiles = [];
    return state;
  });

  builder.addCase(asyncQueries.getCustomerFiles.rejected, (state) => {
    state.loadingCustomerFiles = false;
    return state;
  });

  builder.addCase(asyncQueries.getCustomerFiles.fulfilled, (state, { payload }) => {
    state.loadingCustomerFiles = false;
    state.customerFiles = payload;
    return state;
  });
};